import { useId } from "react";
import { useTranslation } from "react-i18next";
import { DialogShell } from "@/components/ui";
import { type Document } from "@/hooks/use-document-repo";
import { formatBytes, mimeLabel, mimeColor } from "@/lib/file-format";
import { formatTimestampDate } from "@/lib/dates";
import { isProtected } from "@/lib/document-source";
import { WarningIcon, LockClosedIcon } from "@/components/icons";
import { linkedRecordLabel } from "@/components/violations/linked-record-label";
import { DocumentSourceBadge } from "./document-source-badge";

interface DocumentDeleteDialogProps {
  doc: Document | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Source-aware delete confirmation (mockup 04). Names the file, its source
 * badge, and a plain-language consequence specific to the source. Deleting
 * routes (at the call site) to the file's OWN source endpoint. Protected
 * sources (CROMERR custody, sent notices and generated report forms) never
 * reach this dialog — their trash icon is disabled with a tooltip — but as a
 * defensive backstop the Delete button is also disabled if a protected
 * document is somehow passed.
 */
export function DocumentDeleteDialog({
  doc,
  isPending,
  onConfirm,
  onCancel,
}: DocumentDeleteDialogProps) {
  const { t } = useTranslation(["documents", "violations"]);
  const titleId = useId();

  if (!doc) return null;

  const label = mimeLabel(doc.content_type);
  const protectedDoc = isProtected(doc);
  const context =
    linkedRecordLabel(t, doc.context_type, doc.context_label) || t("repo.columns.unfiled");
  // Source-specific consequence copy (form → "removed from submission, kept";
  // task → "removed from task"; document → "removed from {entity}").
  const consequence = t(`repo.delete.consequence.${doc.source}`, { context });

  return (
    <DialogShell open onClose={onCancel} ariaLabelledBy={titleId} maxWidth="max-w-md">
      <div className="mb-3.5 flex gap-3">
        <span
          className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-red-50 text-danger dark:bg-red-900/30"
          aria-hidden="true"
        >
          <WarningIcon className="h-5 w-5" />
        </span>
        <div>
          <h3 id={titleId} className="text-base font-bold text-on-surface">
            {t("repo.delete.title")}
          </h3>
          <p className="text-sm text-muted">{t("repo.delete.subtitle")}</p>
        </div>
      </div>

      {/* File card */}
      <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
        <span
          className={`flex h-6 w-6 flex-none items-center justify-center rounded-md text-[9px] font-bold text-white ${mimeColor(label)}`}
          aria-hidden="true"
        >
          {label}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-on-surface">{doc.filename}</p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {label} · {formatBytes(doc.size_bytes)} · {formatTimestampDate(doc.created_at)}
          </p>
        </div>
        <span className="ml-auto">
          <DocumentSourceBadge source={doc.source} />
        </span>
      </div>

      {/* Consequence */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-on-surface dark:border-amber-900/40 dark:bg-amber-900/20">
        {consequence}
      </div>

      <div className="flex justify-end gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-on-surface hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
        >
          {t("repo.delete.cancel")}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isPending || protectedDoc}
          className="rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:bg-danger/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? t("repo.delete.deleting") : t("repo.delete.confirm")}
        </button>
      </div>

      <p className="mt-3.5 flex items-center gap-1.5 border-t border-gray-100 pt-2.5 text-[11.5px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
        <LockClosedIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        {t("repo.delete.protectedNote")}
      </p>
    </DialogShell>
  );
}
